import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

export default function AnimatedBackground() {
  const symbols = ['Σ', 'π', 'μ', 'σ²', '∫', 'd/dx', 'E[x]', 'P(A|B)', '∂', 'λ', 'β', 'α'];
  const formulaItems = useMemo(
    () =>
      Array.from({ length: 42 }, () => ({
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 10 + Math.random() * 10,
        rot: -15 + Math.random() * 30,
        delay: Math.random() * 4,
        dur: 4 + Math.random() * 4,
        sym: symbols[Math.floor(Math.random() * symbols.length)],
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const candleClusters = useMemo(
    () =>
      Array.from({ length: 8 }, () => {
        const types = ['up', 'down', 'wave', 'zigzag', 'random'];
        const type = types[Math.floor(Math.random() * types.length)];
        return {
          left: Math.min(92, Math.max(2, Math.random() * 100)),
          top: Math.min(92, Math.max(6, Math.random() * 100)),
          count: 8 + Math.floor(Math.random() * 8),
          step: 6 + Math.random() * 6, // px between candles
          maxH: 22 + Math.random() * 12, // container height
          bodyBase: 8 + Math.random() * 8,
          slope: (Math.random() * 2 - 1) * 0.9, // baseline slope
          delay: Math.random() * 3,
          dur: 5 + Math.random() * 3,
          opacity: 0.25 + Math.random() * 0.25,
          type,
          amp: 4 + Math.random() * 8, // vertical amplitude
          freq: 0.6 + Math.random() * 1.2, // for waves
        };
      }),
    []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(rgba(203, 178, 106, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(203, 178, 106, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px',
        }}
      />

      {/* Animated gradient orbs */}
      <motion.div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-[#CBB26A]/6 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.35, 0.55, 0.35],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <motion.div
        className="absolute bottom-0 right-0 w-[720px] h-[720px] bg-[#CBB26A]/4 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.25, 0.45, 0.25],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />

      {/* Subtle stars/particles - Multiple layers with varying sizes */}
      {[...Array(110)].map((_, i) => {
        const size = Math.random() * 2 + 1; // Smaller stars: 1-3px
        const isBrightStar = i % 8 === 0; // Every 8th star is extra bright (less frequent)

        return (
          <motion.div
            key={`star-${i}`}
            className={`absolute rounded-full ${
              isBrightStar
                ? 'bg-[#CBB26A]/70 shadow-[0_0_5px_1.5px_rgba(203,178,106,0.4)]'
                : 'bg-[#CBB26A]/50'
            }`}
            style={{
              width: `${size}px`,
              height: `${size}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              boxShadow: isBrightStar
                ? '0 0 6px 1.6px rgba(203, 178, 106, 0.35)'
                : '0 0 3px 0.6px rgba(203, 178, 106, 0.25)',
            }}
            animate={{
              y: [0, -40, 0],
              opacity: [0.25, 0.55, 0.25],
              scale: [0.85, 1.2, 0.85],
            }}
            transition={{
              duration: 1.8 + Math.random() * 2.5,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: "easeInOut"
            }}
          />
        );
      })}

      {/* Formula fragments (Σ, π, μ, σ², ∫, d/dx) */}
      {formulaItems.map((it, idx) => (
        <motion.div
          key={`fx-${idx}`}
          className="absolute select-none"
          style={{
            left: `${it.left}%`,
            top: `${it.top}%`,
            color: 'rgba(203, 178, 106, 0.35)',
            fontWeight: 600,
            fontSize: `${it.size}px`,
            transform: `rotate(${it.rot}deg)`,
          }}
          animate={{ opacity: [0.0, 0.55, 0.0], y: [0, -8, 0], x: [0, 4, 0] }}
          transition={{ duration: it.dur, repeat: Infinity, delay: it.delay, ease: 'easeInOut' }}
        >
          {it.sym}
        </motion.div>
      ))}

      {/* Consecutive micro candle clusters */}
      {candleClusters.map((cl, i) => (
        <motion.div
          key={`cc-${i}`}
          className="absolute"
          style={{ left: `${cl.left}%`, top: `${cl.top}%`, opacity: cl.opacity }}
          animate={{ x: [0, -12, 0], y: [0, -4, 0] }}
          transition={{ duration: cl.dur, delay: cl.delay, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div style={{ position: 'relative', height: `${cl.maxH}px` }}>
            {Array.from({ length: cl.count }).map((_, j) => {
              const bodyW = 3 + Math.floor(Math.random() * 2);
              const bodyH = Math.max(6, Math.min(cl.maxH - 6, cl.bodyBase + (Math.random() * 6 - 3)));
              const wickH = Math.max(bodyH + 6, bodyH + 6 + Math.floor(Math.random() * 6));
              const bullish = Math.random() > 0.5;
              const offsetY = (() => {
                const a = cl.amp;
                switch (cl.type) {
                  case 'up':
                    return -j * (a * 0.35) + j * cl.slope;
                  case 'down':
                    return j * (a * 0.35) + j * cl.slope;
                  case 'wave':
                    return Math.sin(j * cl.freq) * a + j * cl.slope * 0.2;
                  case 'zigzag':
                    return (j % 2 === 0 ? a * 0.6 : -a * 0.6) + (j - cl.count / 2) * cl.slope * 0.2;
                  case 'random':
                  default:
                    return (Math.sin(j * 0.9) + Math.cos(j * 0.47)) * (a * 0.35);
                }
              })();
              return (
                <div
                  key={`ccb-${i}-${j}`}
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                    width: `${cl.step}px`,
                    height: `${cl.maxH}px`,
                    marginRight: '2px',
                    transform: `translateY(${offsetY.toFixed(1)}px)`,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-0.5px)',
                      width: '1px',
                      height: `${wickH}px`,
                      bottom: 0,
                      background: 'rgba(203, 178, 106, 0.45)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      transform: `translateX(-${bodyW / 2}px)`,
                      width: `${bodyW}px`,
                      height: `${bodyH}px`,
                      bottom: 0,
                      background: bullish ? 'rgba(46, 204, 113, 0.55)' : 'rgba(231, 76, 60, 0.55)',
                      border: '1px solid rgba(203, 178, 106, 0.25)',
                      borderRadius: '2px',
                      boxShadow: '0 0 6px rgba(203,178,106,0.1)',
                    }}
                  />
                </div>
              );
            })}
          </div>
        </motion.div>
      ))}

      {/* Glowing lines */}
      <motion.div
        className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(90deg, transparent 0%, rgba(203, 178, 106, 0.05) 50%, transparent 100%)',
        }}
        animate={{
          x: ['-100%', '100%'],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
      />
    </div>
  );
}
